import { LightbulbIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { q } from "./api.ts";
import { relative } from "./format.ts";
import { LEARNING_STATUS, LESSON_LABELS as L, lessonText, titleText } from "./labels.ts";
import { groupLessons, LESSON_STATUS, statementAddsDetail } from "./lesson-items.ts";
import { EmptyState, ErrorText } from "./ui.tsx";
import { ChannelChip } from "./ui/channel.tsx";
import { GroupHeader, ListRow } from "./ui/list.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip } from "./ui/status.tsx";

// The product's lessons (docs/design-system.md §0.6, Lessons tab): what each
// confirmed reading taught, grouped by what it found. One row per lesson: the
// action it came from, what the lesson says when that adds to the title, its
// channel, how many later actions tested it, and when it was kept. Read-only:
// a lesson changes only through readings the person confirms.

export function Lessons({ productId }: { productId: string }) {
  const results = useQuery(q.results(productId));
  const actions = useQuery(q.actions(productId));
  if (results.isLoading || actions.isLoading) return <Skeleton shape="list" rows={3} />;
  if (!results.data) return <ErrorText error={results.error} />;
  const groups = groupLessons(results.data.learnings, actions.data ?? []);
  if (!groups.length) return <EmptyState icon={<LightbulbIcon size={24} />} title={L.emptyTitle}>{L.emptyNote}</EmptyState>;
  return <div className="z-flow">
    <p className="z-small z-muted">{L.note}</p>
    <div className="z-boxed">{groups.map(({ group, rows }) => <div key={group}>
      <GroupHeader label={L.groups[group]} count={rows.length} status={LESSON_STATUS[group]} />
      {rows.map(({ lesson, action }) => {
        const said = lessonText(lesson.statement);
        const title = action ? titleText(action.title) : said;
        // Only an exception to the usual "hypothesis" gets a chip (§0.3d).
        const status = lesson.status === "supported" || lesson.status === "contradicted" ? LEARNING_STATUS[lesson.status] : null;
        const chips = <>
          {lesson.channel && <ChannelChip channel={lesson.channel} />}
          {lesson.tested_by.actions > 0 && <Chip>{L.tested(lesson.tested_by.actions)}</Chip>}
          {status && <Chip variant="soft" tone={status.tone as "green" | "red"}>{status.label}</Chip>}
        </>;
        return <ListRow key={lesson.id} to={lesson.action_id ? `/p/${productId}/actions/${lesson.action_id}` : undefined}
          status={LESSON_STATUS[group]} title={title}
          sub={action && statementAddsDetail(lesson.statement) && said !== title ? said : undefined}
          chips={chips} meta={relative(lesson.created_at)} />;
      })}
    </div>)}</div>
    <ErrorText error={actions.error} />
  </div>;
}
